import { spacing } from 'ext-ui/style/spacing'
import React, { ComponentProps, useEffect, useState } from 'react'
import FiltersFlyout from 'src/component-flyout/FiltersFlyout/FiltersFlyout'
import { CreditCardDropdownV2Props } from 'src/components/CreditCardDropdownV2/CreditCardDropdownV2'
import UtilityToolbarDownload, {
  UtilityToolbarDownloadProps,
} from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDownload'
import UtilityToolbarDropdown, {
  UtilityToolbarDropdownProps,
} from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarDropdown'
import UtilityToolbarFilterButton from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarFilterButton'
import UtilityToolbarHeader from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarHeader'
import UtilityToolbarSearch, {
  UtilityToolbarSearchProps,
} from 'src/components/DataGrid/UtilityToolbar/UtilityToolbarSearch'
import { FiltersFlyoutProps } from 'src/components/FilterSection/FilterSection'
import { UserDropdownProps } from 'src/components/UserDropdown/UserDropdown'
import variables from 'src/style/variables.json'
import { css } from 'styled-components'

import UtilityToolbarCreditCardDropdown from './UtilityToolbarCreditCardDropdown'

type UtilityToolbarProps = {
  header?: React.ReactElement
  searchProps?: UtilityToolbarSearchProps
  userDropdownProps?: UserDropdownProps
  filtersFlyoutProps: FiltersFlyoutProps
  dateFilterDropdownProps?: UtilityToolbarDropdownProps
  creditCardDropdownProps?: CreditCardDropdownV2Props
  utilityToolbarDropdownProps?: UtilityToolbarDropdownProps
  utilityToolbarAdditionalAction?: React.ReactElement
  utilityToolbarDownloadProps?: UtilityToolbarDownloadProps
} & ComponentProps<'div'>

const UtilityToolbar: React.FC<UtilityToolbarProps> = (
  props,
): React.ReactElement => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  useEffect(() => {
    if (isFiltersOpen && props.filtersFlyoutProps?.onOpenFlyout) {
      props.filtersFlyoutProps.onOpenFlyout()
    }
  }, [isFiltersOpen, props.filtersFlyoutProps.onOpenFlyout])

  const onFilterButtonClick = (): void => {
    setIsFiltersOpen(!isFiltersOpen)
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        padding: ${spacing(4)} ${spacing(6)};
        gap: ${spacing(4)};
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: ${variables.units['base-unit-3']}px;
        `}
      >
        {props.header && (
          <UtilityToolbarHeader>{props.header}</UtilityToolbarHeader>
        )}

        {/* Search bar with flex-grow to fill available space */}
        {props.searchProps && (
          <div
            css={css`
              flex: 1;
            `}
          >
            <UtilityToolbarSearch
              {...props.searchProps}
              noHeader={props.header === undefined}
            />
          </div>
        )}

        {/* Empty spacer if no search bar */}
        {!props.searchProps && (
          <div
            css={css`
              flex: 1;
            `}
          ></div>
        )}

        {/* Right-aligned items container */}
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: ${variables.units['base-unit-3']}px;
            margin-left: auto;
          `}
        >
          {props.dateFilterDropdownProps && (
            <UtilityToolbarDropdown {...props.dateFilterDropdownProps} />
          )}
          {props.creditCardDropdownProps && (
            <UtilityToolbarCreditCardDropdown
              creditCardDropdownProps={props.creditCardDropdownProps}
            />
          )}
          {props.filtersFlyoutProps && (
            <UtilityToolbarFilterButton
              onFilterButtonClick={onFilterButtonClick}
              selectedFiltersCount={
                props.filtersFlyoutProps.selectedFiltersCount || 0
              }
            />
          )}
          {props.utilityToolbarDropdownProps && (
            <UtilityToolbarDropdown {...props.utilityToolbarDropdownProps} />
          )}
          {props.utilityToolbarDownloadProps && (
            <UtilityToolbarDownload {...props.utilityToolbarDownloadProps} />
          )}
          {props.utilityToolbarAdditionalAction}
        </div>
      </div>
      {props.children}
      <FiltersFlyout
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        {...props.filtersFlyoutProps}
      />
    </div>
  )
}

export default UtilityToolbar
